/**
 * LUSTRO mobile/src/constants/recommendationDeviceOptions.ts (kanon) — listy pozycji do
 * odznaczenia w zaleceniach per-lokal (widok PgCogDetails): typy urządzeń, przewody WK / WŁ / Wwc
 * przy kratce (migr. 056), pomieszczenia/urządzenia po „w pomieszczeniu:" przy „Brak Went." (059).
 *
 * Od 2026-10-02 listy są edytowalne w zakładce „Zalecenia" (migracja 054,
 * GET /api/recommendations/device-options); te stałe to fallback, gdy API nie odpowie. Klucz
 * zestawu = id pozycji katalogu (utils/recommendationCatalog.ts); czy pozycja pokazuje listę,
 * mówi jej `devicesMode`.
 * DEFAULT_DEVICE_OPTION_SETS = seed migracji 054 + 056 + 059 + 072 = kanon mobilny — parytet pilnuje
 * backend/test/recommendationDeviceOptionsParity.test.js (literały w kształcie `{ id, label[, protocolText] }`).
 */

export interface DeviceOption {
  id: string;
  label: string;
  /** Skrót wpisywany do treści zalecenia; brak/null ⇒ label. */
  protocolText?: string | null;
  /** false ⇒ pozycja wycofana z listy wyboru (stałe w kodzie są zawsze aktywne). */
  active?: boolean;
}

/** Klucz = id pozycji katalogu zaleceń per-lokal. */
export type DeviceOptionSets = Record<string, DeviceOption[]>;

export const DEFAULT_DEVICE_OPTION_SETS: DeviceOptionSets = {
  pg_cog: [
    { id: 'PG', label: 'PG (Podgrzewacz gazowy)', protocolText: 'PG' },
    { id: 'COG', label: 'COG (Kocioł gazowy)', protocolText: 'COG' },
  ],
  brak_dostepu: [
    { id: 'PG', label: 'PG (Podgrzewacz gazowy)', protocolText: 'PG' },
    { id: 'COG', label: 'COG (Kocioł gazowy)', protocolText: 'COG' },
    { id: 'kociol_paliwo_stale', label: 'Kocioł na paliwo stałe (CO)' },
    { id: 'kociol_olejowy', label: 'Kocioł olejowy' },
    { id: 'kominek', label: 'Kominek' },
    { id: 'piec_kaflowy', label: 'Piec kaflowy' },
    { id: 'piecokuchnia_koza', label: 'Piecokuchnia, koza' },
    { id: 'trzon_kuchenny', label: 'Trzon kuchenny' },
    { id: 'miejscowy_ogrzewacz', label: 'Miejscowy ogrzewacz pomieszczeń' },
    { id: 'przewod_wentylacyjny', label: 'Przewód wentylacyjny (kratka wentylacyjna)' },
    { id: 'wentylator_mechaniczny', label: 'Wentylator mechaniczny' },
    { id: 'okap_kuchenny', label: 'Okap kuchenny' },
    { id: 'piec', label: 'Piec' },
  ],
  wentylator: [
    { id: 'kociol_co', label: 'Kocioł CO' },
    { id: 'miejscowy_ogrzewacz', label: 'Miejscowy ogrzewacz pomieszczeń' },
    { id: 'gazowy_podgrzewacz_cwu', label: 'Gazowy podgrzewacz wody użytkowej' },
    { id: 'kominek', label: 'Kominek' },
  ],
  kratka: [
    { id: 'WK', label: 'WK (Wentylacja kuchni)', protocolText: 'WK' },
    { id: 'WL', label: 'WŁ (Wentylacja łazienki)', protocolText: 'WŁ' },
    { id: 'WWC', label: 'Wwc (Wentylacja WC)', protocolText: 'Wwc' },
  ],
  lacznik: [
    { id: 'PG', label: 'PG (gazowy podgrzewacz CWU)', protocolText: 'PG' },
    { id: 'COG', label: 'COG (kocioł gazowy)', protocolText: 'COG' },
    { id: 'COW', label: 'COW (Kocioł na paliwo stałe)', protocolText: 'COW' },
    { id: 'piec_grzewczy', label: 'Piec grzewczy (miejscowy ogrzewacz pomieszczeń na paliwo stałe)', protocolText: 'Piec grzewczy' },
    { id: 'kominek', label: 'Kominek' },
  ],
  ocena_techniczna: [
    { id: 'gazowy_podgrzewacz_kociol', label: 'Gazowy podgrzewacz/ kocioł' },
    { id: 'kociol_co_paliwo_stale', label: 'Kocioł CO na paliwo stałe' },
    { id: 'piec', label: 'Piec' },
    { id: 'piec_kaflowy', label: 'Piec kaflowy' },
    { id: 'kominek', label: 'Kominek' },
  ],
  brak_went: [
    { id: 'kuchnia', label: 'kuchni' },
    { id: 'lazienka', label: 'łazienki' },
    { id: 'wc', label: 'WC' },
    { id: 'kotla_co', label: 'kotła CO' },
    { id: 'podgrzewacza_cwu', label: 'podgrzewacza gazowego cwu' },
    { id: 'kuchenki_gazowej', label: 'kuchenki gazowej' },
  ],
};

/** Nagłówek listy pozycji, gdy to nie „Typ urządzenia". */
export const DEVICE_SECTION_TITLE: Record<string, string> = {
  kratka: 'Przewód wentylacyjny:',
  brak_went: 'W pomieszczeniu:',
};

/** Poprawna pozycja z API/cache (obrona przed uszkodzonym cache / starszym kształtem). */
const isValidOption = (o: unknown): o is DeviceOption =>
  !!o && typeof o === 'object'
  && typeof (o as DeviceOption).id === 'string' && (o as DeviceOption).id.length > 0
  && typeof (o as DeviceOption).label === 'string' && (o as DeviceOption).label.trim().length > 0;

/**
 * Scala odpowiedź API (`{ sets: {...} }`) albo jej cache z wartościami domyślnymi:
 * zestaw z serwera, który ma co najmniej jedną AKTYWNĄ pozycję, wygrywa w całości;
 * zestaw brakujący/pusty/uszkodzony ⇒ stałe z kodu (o ile są). Zestawy pozycji dodanych
 * w panelu (nieznane w kodzie) przechodzą tak, jak przyszły.
 */
export function mergeDeviceOptionSets(
  apiSets: unknown,
  defaults: DeviceOptionSets = DEFAULT_DEVICE_OPTION_SETS,
): DeviceOptionSets {
  const src = apiSets && typeof apiSets === 'object' ? (apiSets as Record<string, unknown>) : {};
  const out: DeviceOptionSets = {};
  // Bez iterowania Set — web-admin kompiluje do ES5 (bez downlevelIteration).
  const ids = [...Object.keys(defaults), ...Object.keys(src)].filter((id, i, all) => all.indexOf(id) === i);
  for (const id of ids) {
    const raw = src[id];
    const list = Array.isArray(raw) ? raw.filter(isValidOption) : [];
    out[id] = list.some((o) => o.active !== false) ? list : defaults[id] ?? list;
  }
  return out;
}

/** Pozycje do pokazania na liście wyboru (bez wycofanych), w kolejności z serwera. */
export const activeDeviceOptions = (options: DeviceOption[]): DeviceOption[] =>
  options.filter((o) => o.active !== false);
